import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { NauticusLogo, NauticusMark } from "@/components/NauticusLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ShieldCheck, ArrowRight, Users, Clock, HeartHandshake, CheckCircle2, Umbrella,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1511895426328-dc8714191300?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const STEPS = [
  { icon: Users, title: "Tell us who you're protecting", text: "A few simple questions about your family and finances — no health questions." },
  { icon: ShieldCheck, title: "See your protection picture", text: "Plain-language intelligence on what may be worth reviewing." },
  { icon: HeartHandshake, title: "Choose how to continue", text: "Explore coverage yourself or have a licensed advisor help." },
];

export default function LifeEntry() {
  const navigate = useNavigate();
  const [zip, setZip] = useState("");

  const start = () => {
    const params = new URLSearchParams(window.location.search);
    if (zip.trim()) params.set("zip", zip.trim());
    navigate(`/life/assessment?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[#FBFBF9] text-[#111827]">
      <header className="sticky top-0 z-30 bg-[#FBFBF9]/90 backdrop-blur border-b border-[#E5E5E0]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/life" aria-label="Nauticus home"><NauticusLogo /></Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-[#1E3E62] hover:bg-[#EEF2F6]" data-testid="life-nav-signin">Member sign in</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-10 items-center py-12 sm:py-16">
          <div>
            <span className="inline-flex items-center gap-2 text-[#2D6A4F] text-sm font-bold tracking-wide uppercase" data-testid="life-hero-eyebrow">
              <Umbrella className="w-4 h-4" /> Life & Protection
            </span>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0B192C] mt-4 leading-[1.05]">
              Protect the people who count on you.
            </h1>
            <p className="mt-5 text-lg text-[#4B5563] leading-relaxed max-w-xl">
              Nauticus helps you understand what kind of life insurance protection may fit your family —
              in plain language, with no health questions and no pressure.
            </p>

            <div className="mt-8 max-w-md" data-testid="life-hero-start-block">
              <label htmlFor="life-zip" className="block font-display font-bold text-lg text-[#0B192C] mb-2">
                Enter your ZIP code to begin your protection review
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input id="life-zip" value={zip} onChange={(e) => setZip(e.target.value)} maxLength={5}
                  inputMode="numeric" placeholder="ZIP code" className="h-14 text-lg bg-white" data-testid="life-hero-zip-input" />
                <Button size="lg" onClick={start} data-testid="life-hero-start-btn"
                  className="h-14 px-8 text-lg font-bold rounded-full bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620] shrink-0">
                  Start my review <ArrowRight className="w-5 h-5 ml-1" />
                </Button>
              </div>
              <p className="mt-3 text-sm text-[#6B7280] flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#2D6A4F]" /> Takes about 5 minutes • No cost • No obligation
              </p>
            </div>
          </div>

          <div className="relative hidden lg:block">
            <img src={HERO} alt="A family together" className="rounded-2xl w-full h-[440px] object-cover shadow-2xl" />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-xl px-5 py-4 shadow-xl flex items-center gap-3">
              <NauticusMark className="w-8 h-8" />
              <div>
                <p className="text-[#0B192C] font-bold text-sm leading-tight">Protection, guided</p>
                <p className="text-[#6B7280] text-xs">Clarity before you commit</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="bg-white border-t border-[#E5E5E0]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-14">
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="rounded-2xl border-2 border-[#E5E5E0] bg-[#FBFBF9] p-7" data-testid={`life-step-${i}`}>
                  <div className="w-12 h-12 rounded-lg bg-[#0B192C] flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div className="text-[#B89620] font-bold text-sm mb-1">Step {i + 1}</div>
                  <h3 className="font-display text-xl font-bold text-[#0B192C] mb-2">{s.title}</h3>
                  <p className="text-base text-[#4B5563] leading-relaxed">{s.text}</p>
                </div>
              );
            })}
          </div>
          <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
            {["No health or medical questions", "No cost and no obligation", "A licensed advisor when you want one"].map((t) => (
              <li key={t} className="flex items-center gap-2 text-[#111827] font-semibold"><CheckCircle2 className="w-5 h-5 text-[#2D6A4F]" /> {t}</li>
            ))}
          </ul>
        </div>
      </section>

      <footer className="bg-[#0B192C]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <NauticusLogo light />
          <p className="text-[#9FB3C8] text-sm">Nauticus is a benefits technology & enrollment platform. Life products offered through licensed agents.</p>
        </div>
      </footer>
    </div>
  );
}
